import {keyframes, useTheme} from "@mui/material/styles";
import BasePage from "../../baseComponents/BasePage";
import {LinksIconMenu} from "./LinksIconMenu";
import {Box, Stack, Typography} from "@mui/material";
import React from "react";
import {Link} from "react-router-dom";
import {TypographyProps} from "@mui/material";
import {CenterOutDivider} from "../../atomicComponents/CenterOutDivier";


export const CustomLink = ({to, title}) => {
  const theme = useTheme()
  return (
    <Link
      target="_blank"
      rel="noopener noreferrer"
      to={to}
      style={{
        textDecoration: 'none',
        fontWeight: 'bold',
        color: 'darkred',
      }}
    >
      {title}
    </Link>
  );
}

const swipeIn = keyframes`
  from { transform: translateX(-20px); opacity: 0; }
  to   { transform: translateX(0);     opacity: 1; }
`;

type SwipeInTextProps = TypographyProps & {
  /** Delay before the animation starts. If number, treated as seconds. */
  delay?: number | string;
};

export const SwipeInText = ({
  delay = 0,
  sx,
  children,
  ...props
}: SwipeInTextProps)  =>{
  const animationDelay =
    typeof delay === "number" ? `${delay}s` : delay;

  return (
    <Typography
      {...props}
      sx={{
        fontSize: 'medium',
        opacity: 0,
        animation: `${swipeIn} 0.8s ease-out forwards`,
        animationDelay,
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
}

export const About = () => {
  const theme = useTheme();
  return (
    <BasePage>
      <Box width={'100%'} display={'flex'} justifyContent={'flex-end'}>
        <LinksIconMenu sx={{width: 100, justifyContent: 'right'}}/>
      </Box>
      {/*<Stack direction={'row'} spacing={1}>*/}
      <Box
        width={'100%'}
        sx={{
          p: 4,
          borderRadius: '8px',
          bgcolor: 'white',
          justifyContent: 'center',
          alignItems: 'center',
          boxShadow: `
            inset 6px 6px 8px rgba(0, 0, 0, 0.3),
            inset -6px -6px 8px rgba(255, 255, 255, 0.5)
          `,
        }}
      >
        <SwipeInText delay={0}>
          MHDweb is an exploratory data visualization suite, specifically designed to analyze <CustomLink to={'https://www.predsci.com/'} title={'Predictive Science Inc'}/>'s Coronal and Heliospheric <CustomLink to={'https://www.predsci.com/corona/model_desc.html'} title={'MAS'} /> (Magnetodydrodynamic Algorithm outside a Sphere) model solutions, and compare these results with in situ spacecraft data. This web-based interface is an extension of our existing <CustomLink to={'https://www.predsci.com/mhdweb/home.php'} title={'MHDweb (v1)'}/> site. However, with MHDweb (v2), we have worked to significantly expand the range of available model solutions and comparable spacecraft data, while also providing users a much finer degree of control over the visualization generation process.
        </SwipeInText>
        <CenterOutDivider thickness={3} color="grey" animate delay={0.8} sx={{width: '50%', left: '25%'}} />
        <SwipeInText delay={1.0}>
          The full catalogue of publicly available model results – spanning 50 years – is accessible through this site's <CustomLink to={'/mhdweb2/tools/data-access'} title={'Simulation Database'}/>. Here, users can explore a wide range of model types (<i>e.g.</i> thermodynamic, polytropic, wave-turbulence-driven), run types (<i>e.g.</i> steady state, time dependent, collections), and solar events (<i>e.g.</i> Parker encounters, total solar eclipses, solar storms). These simulations can be compared with data products from a similarly wide range of spacecraft missions (<i>e.g.</i> Parker Solar Probe, Solar Orbiter, Stereo A & B), and solar observatories (<i>e.g.</i> Solar Dynamics Observatory, National Solar Observatory, Mauna Loa Solar Observatory).
        </SwipeInText>
        <CenterOutDivider thickness={3} color="grey" animate delay={1.8} sx={{width: '50%', left: '25%'}} />
        <SwipeInText delay={2.0}>
          MHDweb is an ongoing research project. Currently, we support visualization routines (<i>"Tools" </i>) for: Plot1D, Plot2D, Data Products, Time Series Comparisons, Spacecraft Mapping, White Light Plots, Coronagraph Comparisons, EUV & X-Ray Plots, EUV Imager Comparisons, and MAS Run Data Access. We have also provided an extensive list of <CustomLink to={'/mhdweb2/references'} title={'resources, tutorials, and references'}/> to supplement MHDweb's principal visualization functionalities. This site is under active development, with additional features and quality-of-life fixes scheduled for release within the coming months. However, if you encounter any errors or have any questions while interacting with this site, refer to our <CustomLink to={'https://youtube.com/playlist?list=PLFMyiu2nqQCPsmFzsntGEfZOdB7TR03Fq&si=7a8fUU1yx2WVSkS1'} title={'tutorial series'} /> or <CustomLink to={'https://www.predsci.com/portal/contact.php'} title={'reach out directly'}/> to the PSI team.
        </SwipeInText>
        <CenterOutDivider thickness={3} color="grey" animate delay={2.8} sx={{width: '50%', left: '25%'}} />
        <SwipeInText delay={3.0} sx={{textAlign: 'center', fontStyle: 'italic'}}>
          This work is supported by <CustomLink to={'https://www.nasa.gov/'} title={'NASA'} /> and <CustomLink to={'https://www.nsf.gov/'} title={'NSF'} />.
          In particular, we acknowledge support from:
        </SwipeInText>
        <SwipeInText delay={3.4} sx={{textAlign: 'center'}}>
          ❖
        </SwipeInText>
        <SwipeInText delay={3.6} sx={{textAlign: 'center', fontSize: 'small', fontStyle: 'italic'}}>
          NASA Living With a Star Strategic Capabilities program (80NSSC22K0893)
        </SwipeInText>
        <SwipeInText delay={4.0} sx={{textAlign: 'center'}}>
          ❖
        </SwipeInText>
        <SwipeInText delay={4.2} sx={{textAlign: 'center', fontSize: 'small', fontStyle: 'italic'}}>
          Naval Research Laboratory under subcontract N00173-19-C-2003 <br/>(Wide-Field Imager for Parker Solar Probe and The Solar Orbiter Heliospheric Imager, from NASA contract NNG11EK11I)
        </SwipeInText>
        <SwipeInText delay={4.6} sx={{textAlign: 'center'}}>
          ❖
        </SwipeInText>
        <SwipeInText delay={4.8} sx={{textAlign: 'center', fontSize: 'small', fontStyle: 'italic'}}>
          NASA Heliospheric System Observatory Connect program (80NSSC20K1285)
        </SwipeInText>
        <SwipeInText delay={5.2} sx={{textAlign: 'center'}}>
          ❖
        </SwipeInText>
        <SwipeInText delay={5.4} sx={{textAlign: 'center', fontSize: 'small', fontStyle: 'italic'}}>
          NSF PREEVENTS program (ICE-1854790)
        </SwipeInText>
        <SwipeInText delay={5.8} sx={{textAlign: 'center'}}>
          ❖
        </SwipeInText>
        <SwipeInText delay={6.0} sx={{textAlign: 'center', fontSize: 'small', fontStyle: 'italic'}}>
          Johns Hopkins Applied Physics Laboratory from NASA grant 80NSSC21K1860.
      </SwipeInText>
      </Box>
    </BasePage>
  );
}